/**
 * Sanity check for components
 *
 * Each function should start with sc, and should throw an error if the input is invalid.
 */

type SanityCheckContextOption = {
  msg?: string;
  named: {
    __name: string;
  };
};

const extendMessage = (msg: string, options?: SanityCheckContextOption): string => {
  const named = options?.named && options?.named.__name ? options.named.__name : '';
  const contextMsg = options?.msg ? options?.msg : '';
  const error: Array<string> = ['Sanity check failed'];
  if (named) {
    error.push(`In..........: ${named}`);
  }
  if (msg) {
    error.push(`Message.....: ${msg}`);
  }
  if (contextMsg) {
    error.push(`Context.....: ${contextMsg}`);
  }
  return error.join('\n');
};

/** Return the string if it is a string, otherwise throw */
export const scString = (str: unknown, options?: SanityCheckContextOption): string => {
  if (typeof str !== 'string') {
    throw new Error(extendMessage(`Expected string, got ${str}`, options));
  }

  return str;
};

/** Return the string if not empty, otherwise throw */
export const scNonEmptyString = (str: unknown, options?: SanityCheckContextOption): string => {
  const s = scString(str, options);
  if (s.length === 0) {
    throw new Error(extendMessage(`Expected non-empty string, got ${str}`, options));
  }
  return s;
};

/** Return the value if not null or undefined or empty object or empty array or string */
export const scNonEmpty = <T>(
  value: T | null | undefined,
  options?: SanityCheckContextOption
): T => {
  if (value === null || value === undefined) {
    throw new Error(
      extendMessage(`Expected non-null non-undefined, got ${JSON.stringify(value)}`, options)
    );
  }
  if (Array.isArray(value) && value.length === 0) {
    throw new Error(
      extendMessage(`Expected non empty array, got ${JSON.stringify(value)}`, options)
    );
  }

  if (typeof value === 'object' && Object.keys(value).length === 0) {
    throw new Error(
      extendMessage(`Expected non empty object, got ${JSON.stringify(value)}`, options)
    );
  }

  if (typeof value === 'string' && value.length === 0) {
    throw new Error(
      extendMessage(`Expected non empty string, got ${JSON.stringify(value)}`, options)
    );
  }

  return value as T;
};
